// src/frontend/src/pages/MyTrackedStocksPage.js
import React, { useState, useEffect } from "react";
import { AutobuyService } from "../AutobuyService";
import { useAuth } from "../context/AuthContext";
import { StockTable } from "../components/StockTable";
import styles from "../Home.module.css";
import SearchInput from "../components/SearchInput";
import { config } from "../Constants";
import {
    Box, Typography, Paper, CircularProgress,
    Button, Alert, Chip, Card, CardContent
} from "@mui/material";
import { Link } from "react-router-dom";
import AddShoppingCartIcon from '@mui/icons-material/AddShoppingCart';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import { WorkspacePremium as PremiumIcon } from '@mui/icons-material';

const URL = config.url;

export const MyTrackedStocksPage = () => {
    const [stocks, setStocks] = useState([]);
    const [, setAutobuys] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [keyword, setKeyword] = useState("");
    const [userCredits, setUserCredits] = useState(0);
    const { isAuthenticated, isPremium, login } = useAuth();

    const onInputChange = (e) => {
        e.preventDefault();
        setKeyword(e.target.value.toLowerCase());
    };

    useEffect(() => {
        const fetchAutobuyData = async () => {
            if (!isAuthenticated) {
                setLoading(false);
                return;
            }

            try {
                setLoading(true);

                // Get the user's credits
                const credits = await AutobuyService.getUserCredits();
                setUserCredits(credits);

                // Fetch user's autobuys
                const autobuyData = await AutobuyService.getUserAutobuys();
                setAutobuys(autobuyData);

                if (autobuyData.length === 0) {
                    setStocks([]);
                    setLoading(false);
                    return;
                }

                // Extract SKUs
                const skus = autobuyData.map(ab => ab.stockSku);

                // Fetch stock details for each SKU
                const stocksPromises = skus.map(sku =>
                    fetch(`${URL}/api/v1/stocks/sku/${sku}`)
                        .then(res => res.json())
                        .then(data => {
                            if (data.result === "SUCCESS" && data.data) {
                                // Find the autobuy to combine data
                                const autobuy = autobuyData.find(ab => ab.stockSku === sku);
                                return {
                                    ...data.data,
                                    autobuyId: autobuy.id,
                                    isExecuted: autobuy.executed,
                                    executedAt: autobuy.executedAt,
                                    addToCartUrl: autobuy.addToCartUrl || data.data.addToCartUrl
                                };
                            }
                            return null;
                        })
                        .catch(err => {
                            console.error(`Error fetching stock with SKU ${sku}:`, err);
                            return null;
                        })
                );

                const stocksData = await Promise.all(stocksPromises);
                setStocks(stocksData.filter(s => s !== null));
            } catch (error) {
                console.error("Error fetching autobuys:", error);
                setError("Failed to load your autobuys. Please try again.");
            } finally {
                setLoading(false);
            }
        };

        fetchAutobuyData();
        // Refresh data every 30 seconds
        const interval = setInterval(() => fetchAutobuyData(), 30000);
        return () => clearInterval(interval);
    }, [isAuthenticated]);

    if (!isAuthenticated) {
        return (
            <div className={styles.container} style={{ height: 'auto' }}>
                <Paper sx={{ p: 4, textAlign: 'center' }}>
                    <Typography variant="h6" gutterBottom>
                        Sign in to Use Autobuy
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 3 }}>
                        You need to sign in to use the Autobuy feature and automatically reserve GPUs when they become available.
                    </Typography>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={() => login()}
                    >
                        Sign in
                    </Button>
                </Paper>
            </div>
        );
    }

    if (loading) {
        return (
            <div className={styles.container} style={{ height: 'auto' }}>
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                    <CircularProgress />
                </Box>
            </div>
        );
    }

    // Render executed autobuys separately from pending ones
    const executedAutobuys = stocks.filter(stock => stock.isExecuted);
    const pendingAutobuys = stocks.filter(stock => !stock.isExecuted);

    return (
        <div className={styles.container} style={{ height: 'auto' }}>
            {/* Credits Card */}
            <Card sx={{ mb: 4, borderRadius: 2, boxShadow: 2 }}>
                <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Typography variant="h5" component="div">
                            Autobuy Credits
                        </Typography>
                        <Chip
                            label={`${userCredits} Credit${userCredits !== 1 ? 's' : ''}`}
                            color={userCredits > 0 ? "primary" : "error"}
                            sx={{ fontWeight: 'bold' }}
                        />
                    </Box>

                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Each credit allows you to set up one autobuy that will automatically reserve a GPU when it becomes available.
                        Premium subscribers receive one free credit each month.
                    </Typography>

                    <Button
                        variant="contained"
                        color="primary"
                        component={Link}
                        to={isPremium ? "/credits" : "/subscription"}
                        startIcon={isPremium ? <AddShoppingCartIcon /> : <PremiumIcon />}
                    >
                        {isPremium ? "Get More Credits" : "Upgrade to Premium"}
                    </Button>
                </CardContent>
            </Card>

            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            )}

            {stocks.length === 0 && !loading && !error ? (
                <Paper sx={{ p: 4, textAlign: 'center', mt: 2 }}>
                    <Typography variant="h6" gutterBottom>
                        You don't have any autobuys set up
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 3 }}>
                        Set up autobuys to automatically reserve GPUs when they become available.
                    </Typography>
                    <Button
                        variant="contained"
                        component={Link}
                        to="/stocks"
                        startIcon={<ShoppingCartIcon />}
                    >
                        Browse GPUs
                    </Button>
                </Paper>
            ) : (
                <>
                    {/* Pending Autobuys */}
                    {pendingAutobuys.length > 0 && (
                        <>
                            <Typography variant="h5" sx={{ mt: 4, mb: 2 }}>
                                Pending Autobuys
                            </Typography>

                            {stocks.length > 0 && (
                                <div className={styles.inputContainer}>
                                    <div className={styles.counts}>
                                        {pendingAutobuys.length} active autobuy{pendingAutobuys.length !== 1 ? 's' : ''}
                                    </div>
                                    <div className={styles.input}>
                                        <SearchInput placeholder="Filter by Name" onChange={onInputChange} />
                                    </div>
                                </div>
                            )}

                            <StockTable stock={pendingAutobuys.filter(s => s.name.toLowerCase().includes(keyword))} />
                        </>
                    )}

                    {/* Executed Autobuys */}
                    {executedAutobuys.length > 0 && (
                        <>
                            <Typography variant="h5" sx={{ mt: 4, mb: 2 }}>
                                Executed Autobuys
                            </Typography>

                            {executedAutobuys.map(stock => (
                                <Card key={stock.sku} sx={{ mb: 2, borderRadius: 2, boxShadow: 2 }}>
                                    <CardContent>
                                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                            {stock.thumbnailImage && (
                                                <Box sx={{ flexShrink: 0, mr: 2 }}>
                                                    <img
                                                        src={stock.thumbnailImage}
                                                        alt={stock.name}
                                                        style={{ width: 60, height: 60, objectFit: 'contain' }}
                                                    />
                                                </Box>
                                            )}

                                            <Box sx={{ flexGrow: 1 }}>
                                                <Typography variant="h6" component="div">
                                                    {stock.name}
                                                </Typography>

                                                <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                                                    <Chip
                                                        label="Reserved"
                                                        color="success"
                                                        size="small"
                                                        sx={{ mr: 1 }}
                                                    />

                                                    <Typography variant="body2" color="text.secondary">
                                                        Reserved on: {new Date(stock.executedAt).toLocaleDateString()}
                                                    </Typography>
                                                </Box>
                                            </Box>

                                            <Box>
                                                <Button
                                                    variant="contained"
                                                    color="primary"
                                                    href={stock.addToCartUrl}
                                                    target="_blank"
                                                    startIcon={<ShoppingCartIcon />}
                                                >
                                                    Go to Cart
                                                </Button>
                                            </Box>
                                        </Box>
                                    </CardContent>
                                </Card>
                            ))}
                        </>
                    )}
                </>
            )}
        </div>
    );
};